import { Button } from "./ui/button";
import { Heart, Users, TrendingUp, Calendar, Star } from "lucide-react";
import LovePersonality from "../../../assets/images/love-report/Your Love Personality.webp";
import Compatibility from "../../../assets/images/love-report/Compatibility.webp";
import Strengths from "../../../assets/images/love-report/Strengths & Challenges in.webp";
import Forecasts from "../../../assets/images/love-report/Love Forecasts for Your Future.webp";
import Practical from "../../../assets/images/love-report/Practical Remedies.webp";
import loveReportBook from "../../../assets/images/love-report/love-report-book.jpg";

const ReportDetailsSection = () => {
  const reportSections = [
    {
      title: "Your Love Personality",
      description: "This section looks at the way you view love and what you require from a relationship in order to be content. It reveals your emotional patterns, indicating whether you're expressive, shy, or unpredictable in love. Knowing your love personality will help you become more aware of how to build more stable relationships.",
      image: LovePersonality,
      imageLeft: true
    },
    {
      title: "Compatibility Insights",
      description: "The love compatibility report reveals how emotionally, mentally and physically compatible you and your current partner (future matches) it reveals how your astrological energies interact with each other as well as the best and worst attributes. Love compatibility can assist you in making your relationship stronger or help you develop a more suitable mate.",
      image: Compatibility,
      imageLeft: false
    },
    {
      title: "Strengths & Challenges In Your Love Life",
      description: "All relationships can good and can be improved. This section helps you to spot what is good in your present or potential relationship and how you can make it better. Some of these could be communication, trust, or emotional intimacy. The report acts as a blueprint to improve the positives of your love life and overcome any problems.",
      image: Strengths,
      imageLeft: true
    },
    {
      title: "Love Forecasts For Your Future",
      description: "Your personalized Love Report provides you with a preview of what's ahead concerning the ways in which the astrological energies are unfolding in your love life. It highlights the cycles and times that may be on the horizon, setting you up for pivotal moments on your love path. From potential romantic progress to obstacles, this section allows you to plan ahead, allowing you to approach your love life with greater confidence.",
      image: Forecasts,
      imageLeft: false
    },
    {
      title: "Practical Remedies & Solutions",
      description: "Guided by your astrological chart, your soulmate compatibility report prescribes effective remedies and advice to enhance your love life. These remedies may include making changes to increase emotional connection, overcoming relationship challenges, or enhance your encounters with your partner. Whether it's gemstone recommendations, specific dates for important conversations, these remedies offer practical steps to promote a healthy relationship.",
      image: Practical,
      imageLeft: true
    }
  ];

  const targetAudience = [
    {
      icon: Heart,
      title: "Singles",
      description: "Tired of guessing your love life? This is for singles looking for clarity and love confidence."
    },
    {
      icon: TrendingUp,
      title: "Relationship Issues",
      description: "Experiencing love breakdowns, confusion, or uncertainty? We've got ideas."
    },
    {
      icon: Star,
      title: "Self-Discovery",
      description: "Curious about what fate holds? Discover what life brings you in store for you."
    }
  ];

  const handleScrollToPricing = () => {
    const element = document.getElementById("pricing-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="py-12 lg:py-20 bg-background_lv">
      {/* What's Inside Section */}
      <section className="container mx-auto px-4 mb-12 lg:mb-20">
        <div className="text-center mb-8 lg:mb-16">
          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 lg:mb-6">
            What's Inside Your <span className="text-love-pink">Love Report?</span>
          </h2>
          <p className="text-base lg:text-xl text-muted_lv-foreground max-w-4xl mx-auto">
            Your <strong>personalized love report</strong> is an in-depth astrological analysis that gives you
            worthwhile information about your love life, derived from your unique birth details. It
            creates a map of your emotional world and relationships and helps you better
            understand yourself and navigate your love life.
          </p>
        </div>

        <div className="space-y-8 lg:space-y-16">
          {reportSections.map((section, index) => (
            <div
              key={index}
              className={`grid lg:grid-cols-2 gap-6 lg:gap-12 items-center ${section.imageLeft ? "" : "lg:grid-flow-col-dense"
                }`}
            >
              {/* Book Image */}
              <div className={`flex justify-center ${section.imageLeft ? "order-1" : "order-1 lg:order-2"}`}>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-love-pink/20 to-mystical/20 rounded-3xl blur-xl"></div>
                  <img
                    src={section.image}
                    alt="Love Report Book"
                    className="relative h-48 sm:h-56 lg:h-64 w-auto object-contain drop-shadow-xl"
                  />
                </div>
              </div>

              {/* Content */}
              <div className={`${section.imageLeft ? "order-2" : "order-2 lg:order-1"}`}>
                <div className="bg-love-pink/5 border border-love-pink/20 rounded-2xl p-4 lg:p-8">
                  <h3 className="text-xl lg:text-2xl xl:text-3xl font-bold mb-3 lg:mb-4 text-love-pink">
                    {section.title}
                  </h3>
                  <p className="text-muted_lv-foreground leading-relaxed text-sm lg:text-lg">
                    {section.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Who Is This For Section */}
      <section className="container mx-auto px-4">
        <div className="text-center mb-8 lg:mb-16">
          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 lg:mb-6">
            Who Is This <span className="text-love-pink">Love Report</span> For?
          </h2>
          <p className="text-base lg:text-xl text-muted_lv-foreground">
            Complete guidance for your love journey, helping you find clarity and confidence
            along the way.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
          {targetAudience.map((audience, index) => (
            <div
              key={index}
              className="bg-background_lv border_lv border-love-pink/20 rounded-2xl p-4 lg:p-8 text-center hover:shadow_lv-lg transition-all duration-300 hover:scale-105"
            >
              <div className="flex justify-center mb-4 lg:mb-6">
                <div className="p-3 lg:p-4 bg-love-pink/10 rounded-full">
                  <audience.icon className="h-8 w-8 lg:h-12 lg:w-12 text-love-pink" />
                </div>
              </div>
              <h3 className="text-lg lg:text-xl font-bold mb-2 lg:mb-4 text-love-pink">
                {audience.title}
              </h3>
              <p className="text-muted_lv-foreground leading-relaxed text-sm lg:text-base">
                {audience.description}
              </p>
            </div>
          ))}
        </div>

        <div className="text-center mt-8 lg:mt-12">
          <Button onClick={handleScrollToPricing} variant="love" size="lg" className="text-sm lg:text-lg px-8 lg:px-12 py-4 lg:py-6 w-full sm:w-auto">
            Get Your Personalized Love Report Now
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ReportDetailsSection;